import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  TextField,
  Autocomplete,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import DateTimeIntervalFilter from '../common/DateTimeRangeFilter';
import ErrorDialog from '../common/ErrorDialog';
import { getAllLockersQuery, createCCTVVideoRequest } from '../../services/requests';
import { getLastWeeksDate, getYesterdayDate, roundMinutesToNearest30 } from '../../utils';

interface VideoRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onRequestSuccess?: () => void;
}

export default function VideoRequestDialog({ open, onClose, onRequestSuccess }: VideoRequestDialogProps) {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const date = getLastWeeksDate();
    if (date) {
      date.setHours(0, 0, 0, 0); // Set to 12:00 AM
    }
    return date;
  });
  const [endDate, setEndDate] = useState<Date | null>(
    roundMinutesToNearest30(new Date())
  );
  const [selectedLocker, setSelectedLocker] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showError, setShowError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Get all lockers for dropdown
  const { data: lockersData, isLoading: lockersLoading, error: lockersError } = getAllLockersQuery();

  // Reset error state when dialog opens
  useEffect(() => {
    if (open) {
      setShowError(false);
      setErrorMessage('');
    }
  }, [open]);

  const handleSend = async () => {
    if (!startDate || !endDate || !selectedLocker) {
      return;
    }

    // Reset error state before attempting to send
    setShowError(false);
    setErrorMessage('');
    setIsLoading(true);

    try {
      const payload = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        lockerCode: selectedLocker.lockerCode
      };

      const result = await createCCTVVideoRequest(payload);      
      if (result.success && result.status === 404) {
        // Request sent to locker successfully        
        // Reset form to default values and close
        const defaultStartDate = getLastWeeksDate();
        if (defaultStartDate) {
          defaultStartDate.setHours(0, 0, 0, 0);
        }
        setStartDate(defaultStartDate);
        setEndDate(roundMinutesToNearest30(new Date()));
        setSelectedLocker(null);
        
        // Refresh the table if callback is provided
        if (onRequestSuccess) {
          onRequestSuccess();
        }
        onClose();
      } else if (result.status === 409) {
        // Show error dialog for duplicate request
        setErrorMessage(t('duplicate_video_request') as string);
        setShowError(true);
      }
    } catch (error) {
      setErrorMessage(t('sending_video_general_error') as string);
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="customDialog"
      sx={{
        display: 'flex',
        justifyContent: 'center',
      }}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: { borderRadius: 5 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {t('send_video_request')}
        <IconButton
          size="small"
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mt: 1, mb: 1 }}>
            <InputLabel sx={{ paddingRight: 1, minWidth: 110 }}>{t('select_locker_code')}</InputLabel>
            <Autocomplete
              options={lockersData?.data?.filter((locker: any) => locker.lockerType === 2) || []}
              getOptionLabel={(locker) => locker.lockerCode}
              value={selectedLocker}
              onChange={(event, value) => setSelectedLocker(value)}
              disabled={lockersLoading}
              loading={lockersLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder=""
                  sx={{ width: 250 }}
                  error={!!lockersError}
                  helperText={lockersError ? 'Error loading lockers' : undefined}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {lockersLoading ? <div>Loading...</div> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              filterOptions={(options, { inputValue }) =>
                options.filter((locker) =>
                  locker.lockerCode.toLowerCase().includes(inputValue.toLowerCase())
                )
              }
            />
          </Box>
          
          <DateTimeIntervalFilter
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            startDate={startDate}
            endDate={endDate}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!startDate || !endDate || !selectedLocker || isLoading}
          startIcon={<SendIcon />}
          sx={{ textTransform: 'none' }}
        >
          {isLoading ? t('sending') : t('send')}
        </Button>
      </DialogActions>
      
      <ErrorDialog 
        message={errorMessage}
        status={showError}
      />
    </Dialog>
  );
}